import React, { useCallback, useEffect, useRef, useState } from "react";

import { cx, useTranslation } from "utils";

import { Icon } from "components";

type UseHorizontalScrollFadeResult = {
  scrollRef: React.RefObject<HTMLDivElement>;
  hasOverflow: boolean;
  atEnd: boolean;
  isHovered: boolean;
  setIsHovered: (hovered: boolean) => void;
  scrollForward: () => void;
  scrollToStart: () => void;
};

export const useHorizontalScrollFade = (): UseHorizontalScrollFadeResult => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [atEnd, setAtEnd] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const syncScrollState = useCallback(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) return;

    const overflow = scrollElement.scrollWidth - scrollElement.clientWidth > 1;
    setHasOverflow(overflow);
    setAtEnd(!overflow || scrollElement.scrollLeft + scrollElement.clientWidth >= scrollElement.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) return undefined;

    syncScrollState();

    const resizeObserver = new ResizeObserver(() => syncScrollState());
    resizeObserver.observe(scrollElement);

    scrollElement.addEventListener("scroll", syncScrollState, { passive: true });
    window.addEventListener("resize", syncScrollState);

    return () => {
      resizeObserver.disconnect();
      scrollElement.removeEventListener("scroll", syncScrollState);
      window.removeEventListener("resize", syncScrollState);
    };
  }, [syncScrollState]);

  const scrollForward = useCallback(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) return;
    scrollElement.scrollBy({ left: scrollElement.clientWidth, behavior: "smooth" });
  }, []);

  const scrollToStart = useCallback(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) return;
    scrollElement.scrollTo({ left: 0, behavior: "smooth" });
  }, []);

  return { scrollRef, hasOverflow, atEnd, isHovered, setIsHovered, scrollForward, scrollToStart };
};

type ScrollEdgeProps = {
  direction: "forward" | "back";
  /** Horizontal inset from the edge (rem) so the chrome clears any sticky columns. */
  offsetRem?: number;
  onClick: () => void;
  dataTestId: string;
};

const ScrollEdge = ({ direction, offsetRem, onClick, dataTestId }: ScrollEdgeProps) => {
  const isBack = direction === "back";
  const style = isBack ? { left: `${offsetRem ?? 0}rem` } : offsetRem ? { right: `${offsetRem}rem` } : undefined;

  return (
    <div className={cx("outcome-comparison-scroll-fade__chrome", isBack && "outcome-comparison-scroll-fade__chrome--start")} style={style} aria-hidden>
      <div className={cx("outcome-comparison-scroll-fade__gradient", isBack && "outcome-comparison-scroll-fade__gradient--start")} />
      <button
        type="button"
        className={cx("outcome-comparison-scroll-fade__arrow", isBack && "outcome-comparison-scroll-fade__arrow--start")}
        onClick={onClick}
        tabIndex={-1}
        data-testid={dataTestId}
      >
        <Icon iconName={isBack ? "tabler/arrow-left" : "tabler/arrow-right"} size="s" />
      </button>
    </div>
  );
};

type HorizontalScrollFadeProps = {
  children: React.ReactNode;
  className?: string;
  dataTestId?: string;
  /** Inset (rem) for the right-edge "scroll forward" chrome, so it sits just left of sticky columns. */
  chromeOffsetRight?: number;
  /** Inset (rem) for the left-edge "back to start" chrome, so it sits just right of the sticky left column. */
  chromeOffsetLeft?: number;
  enabled?: boolean;
};

export const HorizontalScrollFade = ({
  children,
  className,
  dataTestId = "horizontal-scroll-fade",
  chromeOffsetRight,
  chromeOffsetLeft,
  enabled = true,
}: HorizontalScrollFadeProps) => {
  const { t } = useTranslation("scenarioAnalysis");
  const { scrollRef, hasOverflow, atEnd, isHovered, setIsHovered, scrollForward, scrollToStart } = useHorizontalScrollFade();

  const showChrome = enabled && hasOverflow;
  const ariaLabel = t("outcomeComparison.scrollRegionLabel");

  const wrapperClassName = cx(
    "outcome-comparison-scroll-fade",
    showChrome && "outcome-comparison-scroll-fade--overflow",
    showChrome && isHovered && "outcome-comparison-scroll-fade--hovered",
    className
  );

  return (
    <div
      className={wrapperClassName}
      data-testid={dataTestId}
      data-has-scroll={showChrome ? "true" : "false"}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div ref={scrollRef} className="outcome-comparison-scroll-fade__viewport" role="region" aria-label={ariaLabel}>
        {children}
      </div>
      {showChrome && !atEnd ? (
        <ScrollEdge direction="forward" offsetRem={chromeOffsetRight} onClick={scrollForward} dataTestId={`${dataTestId}-arrow`} />
      ) : null}
      {showChrome && atEnd ? (
        <ScrollEdge direction="back" offsetRem={chromeOffsetLeft} onClick={scrollToStart} dataTestId={`${dataTestId}-arrow-start`} />
      ) : null}
    </div>
  );
};

export default HorizontalScrollFade;
